import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Pagination,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Search as SearchIcon,
  Business as BusinessIcon,
  BarChart as BarChartIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  DateRange as DateRangeIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { format, parseISO, startOfMonth, endOfMonth, subMonths, isWithinInterval, 
  startOfWeek, endOfWeek, subWeeks, addDays, eachDayOfInterval } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import api from '../api';
import logger from '../utils/logger';
import { useAuth } from '../context/AuthContext'; // Import the auth context

const ResearchStatistics = () => {
  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [researchLogs, setResearchLogs] = useState([]); // For table display (paginated)
  const [allLogsForStats, setAllLogsForStats] = useState([]); // For statistics (all matching records)
  const [statistics, setStatistics] = useState(null);
  const [userStatistics, setUserStatistics] = useState({});
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10); // Make it state-controlled
  const [filters, setFilters] = useState({
    username: '',
    company: '',
    dateRange: 'thisMonth',
    chartDateRange: 'thisMonth',
    success: '',
    research_type: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    username: '',
    company: '',
    dateRange: 'thisMonth',
    chartDateRange: 'thisMonth',
    success: '',
    research_type: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const { isAdmin, isSupervisor, user, getCompanyResearchLogs, getUsers } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamChecked, setTeamChecked] = useState(false);

  // Constants
  // Extended color palette with 20 distinct colors for better user differentiation
  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', // Original colors
    '#8884d8', '#82ca9d', '#ffc658', '#a4de6c', // Additional colors
    '#d0ed57', '#83a6ed', '#8dd1e1', '#5C7BD9', 
    '#FF6E5A', '#8A2BE2', '#FF7F50', '#20B2AA',
    '#FF69B4', '#6495ED', '#9ACD32', '#00CED1'
  ];
  
  // Function to get a consistent color for a username
  const getUserColor = (username) => {
    // Create a hash of the username to get a consistent index
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Make sure the hash is positive
    hash = Math.abs(hash);
    // Use modulo to get an index in the COLORS array
    return COLORS[hash % COLORS.length];
  };
  
  // Ensure statistics and graphs update when data or chart filter changes
  useEffect(() => {
    if (allLogsForStats.length > 0) {
      generateStatistics();
    }
  }, [allLogsForStats, appliedFilters.chartDateRange]);

  // Load data on component mount
  useEffect(() => {
    const initialLoad = async () => {
      // First load the users (this is fast)
      await fetchAllData();
      
      // Then immediately apply the filters to load the data with the default "thisMonth" setting
      await fetchFilteredLogs(1);
    };
    
    initialLoad();
  }, [isAdmin, isSupervisor]);

  // Load data when filters change
  useEffect(() => {
    // Only fetch when the component is mounted (not during initial loading)
    if (!loading || currentPage > 1) {
      fetchFilteredLogs(currentPage);
    }
  }, [appliedFilters, currentPage]);

  // Update statistics when chartDateRange changes
  useEffect(() => {
    if (allLogsForStats.length > 0) {
      logger.log(`Chart date range changed to: ${appliedFilters.chartDateRange}, regenerating statistics...`);
      generateStatistics();
    }
  }, [allLogsForStats, appliedFilters.chartDateRange]);

  // Fetch team members for supervisor on mount
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (isSupervisor) {
        try {
          const response = await api.getAssignedUsers();
          if (response.success && Array.isArray(response.users)) {
            // Include the supervisor themselves in the team members list
            const teamWithSupervisor = [...response.users];
            // Check if supervisor is already in the list to avoid duplicates
            const supervisorInTeam = teamWithSupervisor.find(member => member.username === user?.username);
            if (!supervisorInTeam && user?.username) {
              teamWithSupervisor.push({
                username: user.username,
                email: user.email || '',
                id: user.id || user.username
              });
            }
            setTeamMembers(teamWithSupervisor);
          } else {
            // If no team members, at least include the supervisor themselves
            setTeamMembers(user?.username ? [{
              username: user.username,
              email: user.email || '',
              id: user.id || user.username
            }] : []);
          }
        } catch (err) {
          // If error fetching team, at least include the supervisor themselves
          setTeamMembers(user?.username ? [{
            username: user.username,
            email: user.email || '',
            id: user.id || user.username
          }] : []);
        } finally {
          setTeamChecked(true);
        }
      } else {
        setTeamChecked(true);
      }
    };
    fetchTeamMembers();
  }, [isSupervisor, user]);

  // Fetch all required data
  const fetchAllData = async () => {
    try {
      // Fetch users for filtering
      if (isAdmin || isSupervisor) {
        try {
          // Get users for the dropdown
          const usersResponse = await getUsers();
          if (usersResponse.success) {
            if (isSupervisor) {
              // For supervisors, only show their team members (including themselves)
              // We'll use teamMembers for the dropdown instead of all users
              // This will be handled in the dropdown rendering
              setUsers(usersResponse.users);
            } else {
              // For admins, show all users
              setUsers(usersResponse.users);
            }
          }
        } catch (err) {
          logger.error('Error loading user data:', err);
        }
      }

      // We don't need to load research logs here anymore as fetchFilteredLogs will do that
      // with the proper date filtering already applied
      logger.log('Finished loading initial user data');
      return true;
    } catch (err) {
      logger.error('Error loading research statistics:', err);
      setError('Failed to load research data. Please try again later.');
      return false;
    }
  };

  // Function to get data filtered by the chart date range
  const getFilteredDataForCharts = () => {
    if (!allLogsForStats || allLogsForStats.length === 0) {
      logger.log('No logs available for filtering');
      return [];
    }
    
    logger.log(`Filtering ${allLogsForStats.length} logs by chart date range: ${appliedFilters.chartDateRange}`);
    
    // If chartDateRange is 'all', return all data
    if (appliedFilters.chartDateRange === 'all') {
      logger.log('Returning all logs (no date filtering)');
      return [...allLogsForStats];
    }
    
    // Apply date range filter based on chartDateRange
    const now = new Date();
    let startDate, endDate;
    
    switch (appliedFilters.chartDateRange) {
      case 'thisWeek':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'lastWeek':
        startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'lastMonth':
        startDate = startOfMonth(subMonths(now, 1));
        endDate = endOfMonth(subMonths(now, 1));
        break;
      case 'last3Months':
        startDate = startOfMonth(subMonths(now, 2));
        endDate = endOfMonth(now);
        break;
      default:
        // Default to all time - don't apply date filtering
        logger.log('Unknown date range, returning all logs');
        return [...allLogsForStats];
    }
    
    logger.log(`Date range: ${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`);
    
    // Filter logs by date range
    const filteredLogs = allLogsForStats.filter(log => {
      if (!log.timestamp) {
        logger.warn('Log missing timestamp:', log);
        return false;
      }
      
      try {
        const logDate = new Date(log.timestamp);
        if (isNaN(logDate.getTime())) {
          logger.warn('Invalid timestamp in log:', log.timestamp);
          return false;
        }
        
        const isInRange = isWithinInterval(logDate, { start: startDate, end: endDate });
        return isInRange;
      } catch (err) {
        logger.error('Error processing log timestamp:', err, log);
        return false;
      }
    });
    
    logger.log(`Filtered logs by date: ${filteredLogs.length} out of ${allLogsForStats.length}`);
    return filteredLogs;
  };

  // Generate statistics from the logs
  const generateStatistics = () => {
    // Get data filtered by the chart date range
    const filteredLogs = getFilteredDataForCharts();
    logger.log('Generating statistics with filtered logs:', filteredLogs);
    
    // Use filtered logs for statistics
    if (!filteredLogs || filteredLogs.length === 0) {
      logger.log('No filtered logs available, setting empty statistics');
      setStatistics({
        total_searches: 0,
        successful_searches: 0,
        success_rate: 0,
        by_user: {},
        by_day: []
      });
      return;
    }

    // Calculate basic statistics
    const totalSearches = filteredLogs.length;
    const successfulSearches = filteredLogs.filter(log => log.success).length;
    const successRate = totalSearches > 0 ? (successfulSearches / totalSearches) * 100 : 0;

    logger.log(`Statistics calculated: totalSearches=${totalSearches}, successfulSearches=${successfulSearches}, successRate=${successRate.toFixed(2)}%`);

    // Group by user
    const byUser = {};
    logger.log('Processing logs for user statistics. Sample logs:');
    filteredLogs.slice(0, 5).forEach((log, index) => {
      logger.log(`Log ${index + 1}:`, {
        username: log.username,
        user_id: log.user_id,
        company_name: log.company_name,
        timestamp: log.timestamp
      });
    });
    
    filteredLogs.forEach(log => {
      const username = log.username || 'Anonymous';
      if (!byUser[username]) {
        byUser[username] = {
          total: 0,
          successful: 0
        };
      }
      byUser[username].total++;
      if (log.success) {
        byUser[username].successful++;
      }
    });

    // Calculate per-user statistics
    Object.keys(byUser).forEach(username => {
      const user = byUser[username];
      user.success_rate = user.total > 0 ? (user.successful / user.total) * 100 : 0;
    });

    // Group by day
    const byDay = {};
    filteredLogs.forEach(log => {
      if (!log.timestamp) {
        logger.warn('Log missing timestamp:', log);
        return;
      }
      
      const date = new Date(log.timestamp);
      if (isNaN(date.getTime())) {
        logger.warn('Invalid timestamp in log:', log.timestamp);
        return;
      }
      
      const dateStr = format(date, 'yyyy-MM-dd');
      
      if (!byDay[dateStr]) {
        byDay[dateStr] = {
          date: dateStr,
          total: 0,
          successful: 0
        };
      }
      
      byDay[dateStr].total++;
      if (log.success) {
        byDay[dateStr].successful++;
      }
    });

    // Convert byDay to array and sort by date
    const byDayArray = Object.values(byDay).sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });

    const statsObject = {
      total_searches: totalSearches,
      successful_searches: successfulSearches,
      success_rate: successRate.toFixed(2),
      by_user: byUser,
      by_day: byDayArray
    };
    
    logger.log('Setting statistics object:', statsObject);
    setStatistics(statsObject);

    // Generate per-user statistics
    const userStats = {};
    Object.keys(byUser).forEach(username => {
      userStats[username] = {
        total_searches: byUser[username].total,
        successful_searches: byUser[username].successful,
        success_rate: byUser[username].success_rate.toFixed(2)
      };
    });

    logger.log('Generated userStats object:', userStats);
    logger.log('User names found in logs:', Object.keys(byUser));
    setUserStatistics(userStats);
    logger.log(`Generated statistics for ${filteredLogs.length} logs with chart date range: ${appliedFilters.chartDateRange}`);
    logger.log('Current user from auth context:', user);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    logger.log(`Filter changed: ${name} = ${value}`);
    
    // If changing chartDateRange, also update dateRange to match
    if (name === 'chartDateRange') {
      const newFilters = {
        ...filters,
        chartDateRange: value,
        dateRange: value // keep dateRange in sync with chartDateRange
      };
      setFilters(newFilters);
      const newAppliedFilters = {
        ...appliedFilters,
        chartDateRange: value,
        dateRange: value // keep dateRange in sync with chartDateRange
      };
      setAppliedFilters(newAppliedFilters);
      // Schedule statistics recalculation for after the state update
      setTimeout(() => {
        logger.log('Regenerating statistics due to chartDateRange change');
        generateStatistics();
      }, 0);
    } else {
      // For other filters, apply them the normal way
      const newFilters = {
        ...filters,
        [name]: value
      };
      setFilters(newFilters);
      setAppliedFilters(newFilters);
      setCurrentPage(1);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      username: '',
      company: '',
      dateRange: 'thisMonth',
      chartDateRange: 'thisMonth',
      success: '',
      research_type: ''
    });
    setAppliedFilters({
      username: '',
      company: '',
      dateRange: 'thisMonth',
      chartDateRange: 'thisMonth',
      success: '',
      research_type: ''
    });
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchFilteredLogs(value);
  };

  // Fetch logs with filters
  const fetchFilteredLogs = async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const apiFilters = { ...appliedFilters };
      
      // If dateRange is not "all", convert it to start_date and end_date
      if (apiFilters.dateRange !== 'all') {
        const now = new Date();
        let startDate, endDate;
        
        switch (apiFilters.dateRange) {
          case 'thisWeek':
            startDate = startOfWeek(now, { weekStartsOn: 1 });
            endDate = endOfWeek(now, { weekStartsOn: 1 });
            break;
          case 'lastWeek':
            startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
            endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
            break;
          case 'thisMonth':
            startDate = startOfMonth(now);
            endDate = endOfMonth(now);
            break;
          case 'lastMonth':
            startDate = startOfMonth(subMonths(now, 1));
            endDate = endOfMonth(subMonths(now, 1));
            break;
          case 'last3Months':
            startDate = startOfMonth(subMonths(now, 2));
            endDate = endOfMonth(now);
            break;
          default:
            // Default to all time - don't set any dates
            startDate = null;
            endDate = null;
            break;
        }
        
        if (startDate && endDate) {
          // Format dates in YYYY-MM-DD format as required by the backend
          apiFilters.start_date = format(startDate, 'yyyy-MM-dd');
          apiFilters.end_date = format(endDate, 'yyyy-MM-dd');
          logger.log(`Date range: ${apiFilters.start_date} to ${apiFilters.end_date}`);
        }
      } else {
        // "all" was selected, make sure to remove any existing date filters
        delete apiFilters.start_date;
        delete apiFilters.end_date;
      }
      
      // Handle the success filter properly
      if (apiFilters.success === '') {
        // If empty string (All), don't include in API call
        delete apiFilters.success;
      }
      
      // Remove dateRange and chartDateRange from API filters as they're not recognized by the API
      delete apiFilters.dateRange;
      delete apiFilters.chartDateRange;
      
      logger.log(`Fetching logs with filters: ${JSON.stringify(apiFilters)}, page: ${page}, per_page: ${recordsPerPage}`);
      
      try {
        // Fetch paginated data for the table display
        const paginatedResponse = await getCompanyResearchLogs({
          ...apiFilters,
          page,
          per_page: recordsPerPage
        });
        
        logger.log('Paginated API Response:', paginatedResponse);
        
        if (paginatedResponse.success) {
          // Check if logs is defined and is an array
          if (Array.isArray(paginatedResponse.logs)) {
            setResearchLogs(paginatedResponse.logs);
            setTotalRecords(paginatedResponse.total || paginatedResponse.logs.length);
            setTotalPages(paginatedResponse.pages || Math.ceil(paginatedResponse.logs.length / recordsPerPage));
            
            // Now fetch all data for statistics (only if on first page to avoid duplicate fetches)
            if (page === 1) {
              logger.log('Fetching all data for statistics...');
              
              try {
                // Fetch all data with the same filters but a large per_page to get everything
                // This is a separate API call to get all the data for statistics
                const allDataResponse = await getCompanyResearchLogs({
                  ...apiFilters,
                  page: 1,
                  per_page: 1000 // Use a large number to try to get all records in one call
                });
                
                if (allDataResponse.success && Array.isArray(allDataResponse.logs)) {
                  logger.log(`Fetched ${allDataResponse.logs.length} total logs for statistics`);
                  setAllLogsForStats(allDataResponse.logs);
                  // Update statistics based on all data
                  generateStatistics();
                } else {
                  logger.error('Invalid response format for all logs:', allDataResponse);
                  // Use paginated data as fallback
                  setAllLogsForStats(paginatedResponse.logs);
                  generateStatistics();
                }
              } catch (err) {
                logger.error('Error fetching all logs:', err);
                // Use paginated data as fallback
                setAllLogsForStats(paginatedResponse.logs);
                generateStatistics();
              }
            }
          } else {
            logger.error('Invalid logs data in response:', paginatedResponse);
            setError('Invalid response format: logs is not an array');
          }
        } else {
          setError('Failed to fetch research logs');
        }
      } catch (err) {
        logger.error('Error making API request:', err);
        setError('Failed to communicate with the server');
      }
    } catch (err) {
      logger.error('Error fetching filtered logs:', err);
      setError('Failed to fetch research logs');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle opening log detail dialog
  const handleViewLogDetails = (log) => {
    setSelectedLog(log);
    setLogDialogOpen(true);
  };
  
  // Function to close log detail dialog
  const handleCloseLogDialog = () => {
    setLogDialogOpen(false);
    setSelectedLog(null);
  };

  // Add manual reload function
  const handleManualReload = async () => {
    setLoading(true);
    try {
      // First load the users and basic data
      await fetchAllData();
      
      // Then apply the filters to load the data with the current date filter setting
      await fetchFilteredLogs(1);
    } catch (error) {
      logger.error('Error during manual reload:', error);
      setError('Failed to reload data: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Function to get daily activity data for users
  const getUserDailyActivity = () => {
    // If no logs, return an empty array
    if (!allLogsForStats || allLogsForStats.length === 0) {
      return [];
    }
    
    // Apply date range filter
    let filteredLogs = [...allLogsForStats];
    const now = new Date();
    let startDate, endDate;
    
    switch (appliedFilters.chartDateRange) {
      case 'thisWeek':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'lastWeek':
        startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'lastMonth':
        startDate = startOfMonth(subMonths(now, 1));
        endDate = endOfMonth(subMonths(now, 1));
        break;
      case 'last3Months':
        startDate = startOfMonth(subMonths(now, 2));
        endDate = endOfMonth(now);
        break;
      default:
        // For "all", limit to last 30 days to keep the chart readable
        startDate = subMonths(now, 1);
        endDate = now;
    }
    
    // Filter logs by date range
    filteredLogs = filteredLogs.filter(log => {
      const logDate = new Date(log.timestamp);
      return isWithinInterval(logDate, { start: startDate, end: endDate });
    });
    
    // Generate all dates in the interval
    const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
    
    // Create a map of date -> user activity
    const dailyData = {};
    
    // Initialize the data structure for all days
    dateRange.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      dailyData[dateStr] = { date: dateStr };
      
      // Initialize all users with 0 count
      Object.keys(userStatistics).forEach(username => {
        dailyData[dateStr][username] = 0;
      });
    });
    
    // Count researches for each user on each day
    filteredLogs.forEach(log => {
      const logDate = new Date(log.timestamp);
      const dateStr = format(logDate, 'yyyy-MM-dd');
      const username = log.username || 'Anonymous';
      
      if (dailyData[dateStr]) {
        if (dailyData[dateStr][username] === undefined) {
          dailyData[dateStr][username] = 1;
        } else {
          dailyData[dateStr][username]++;
        }
      }
    });
    
    // Convert to array for the chart and sort by date
    return Object.values(dailyData).sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM d, yyyy h:mm a');
    } catch (err) {
      return dateString;
    }
  };

  // If loading
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Loading research statistics...</Alert>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  // If error
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={handleManualReload}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Supervisor with no team notification */}
      {isSupervisor && teamChecked && teamMembers.length <= 1 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You currently have no team members assigned (only your own statistics are shown). Please contact an admin to request members to be added to your team.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Company Research Statistics</Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={handleManualReload}
          disabled={loading}
        >
          Reload Data
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="User Research Stats" />
        <Tab label="Research History" />
      </Tabs>

      {/* Overview Tab */}
      {tabValue === 0 && statistics && (
        <Grid container spacing={3}>
          {/* Date Filter for Overview */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                  <DateRangeIcon sx={{ mr: 1 }} /> 
                  Date Range
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter Charts</InputLabel>
                    <Select
                      name="chartDateRange"
                      value={filters.chartDateRange}
                      label="Filter Charts"
                      onChange={handleFilterChange}
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="thisWeek">This Week</MenuItem>
                      <MenuItem value="lastWeek">Last Week</MenuItem>
                      <MenuItem value="thisMonth">This Month</MenuItem>
                      <MenuItem value="lastMonth">Last Month</MenuItem>
                      <MenuItem value="last3Months">Last 3 Months</MenuItem>
                    </Select>
                  </FormControl>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={resetFilters}
                    startIcon={<RefreshIcon />}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Summary Cards */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Research Queries
                </Typography>
                <Typography variant="h4">
                  {statistics.total_searches}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Successful Queries
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4">
                    {statistics.successful_searches}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                    ({statistics.success_rate}%)
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Unique Companies Researched
                </Typography>
                <Typography variant="h4">
                  {new Set(getFilteredDataForCharts().map(log => log.company_name)).size}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Charts */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  Research Success Rate
                </Typography>
                {appliedFilters.chartDateRange !== 'all' && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                     appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                     appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                     appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                     appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
                  </Typography>
                )}
              </Box>
              {/* Check if we have filtered data using the chart filter */}
              {getFilteredDataForCharts().length > 0 ? (
                <ResponsiveContainer width="100%" height="90%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Successful', value: statistics.successful_searches },
                        { name: 'Failed', value: statistics.total_searches - statistics.successful_searches }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      <Cell key="cell-0" fill="#00C49F" />
                      <Cell key="cell-1" fill="#FF8042" />
                    </Pie>
                    <ChartTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No data available for the selected date range
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={resetFilters}
                    startIcon={<DateRangeIcon />}
                  >
                    Reset Date Filter
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  Research Trend
                </Typography>
                {appliedFilters.chartDateRange !== 'all' && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                     appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                     appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                     appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                     appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
                  </Typography>
                )}
              </Box>
              {/* Check if we have day-by-day data from statistics */}
              {statistics.by_day && statistics.by_day.length > 0 ? (
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart
                    data={statistics.by_day}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      angle={-45} 
                      textAnchor="end" 
                      height={60}
                      tickFormatter={(date) => format(new Date(date), 'MMM d')}
                    />
                    <YAxis />
                    <ChartTooltip 
                      formatter={(value, name) => [value, name === 'total' ? 'Total Searches' : 'Successful Searches']}
                      labelFormatter={(date) => format(new Date(date), 'EEEE, MMMM d, yyyy')}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      name="Total Searches"
                      stroke="#8884d8" 
                      activeDot={{ r: 8 }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="successful" 
                      name="Successful Searches"
                      stroke="#82ca9d" 
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No data available for the selected date range
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={resetFilters}
                    startIcon={<DateRangeIcon />}
                  >
                    Reset Date Filter
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* User Research Stats Tab */}
      {tabValue === 1 && (
        <>
          {/* Date Filter for User Research Stats */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                <DateRangeIcon sx={{ mr: 1 }} /> 
                Date Range
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Filter Charts</InputLabel>
                  <Select
                    name="chartDateRange"
                    value={filters.chartDateRange}
                    label="Filter Charts"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="thisWeek">This Week</MenuItem>
                    <MenuItem value="lastWeek">Last Week</MenuItem>
                    <MenuItem value="thisMonth">This Month</MenuItem>
                    <MenuItem value="lastMonth">Last Month</MenuItem>
                    <MenuItem value="last3Months">Last 3 Months</MenuItem>
                  </Select>
                </FormControl>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={resetFilters}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Daily User Research Chart */}
          <Paper sx={{ p: 2, mb: 3, height: 400 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Daily Research Activity by User
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {appliedFilters.chartDateRange === 'all' ? 'Last 30 Days' : 
                 appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                 appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                 appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                 appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                 appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
              </Typography>
            </Box>
            {getFilteredDataForCharts().length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart 
                  data={getUserDailyActivity()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    angle={-45} 
                    textAnchor="end" 
                    height={60}
                    tickFormatter={(date) => format(new Date(date), 'MMM d')}
                  />
                  <YAxis />
                  <ChartTooltip 
                    formatter={(value, name) => [value, `User: ${name}`]}
                    labelFormatter={(date) => format(new Date(date), 'EEEE, MMMM d, yyyy')}
                  />
                  <Legend />
                  {Object.keys(userStatistics).map((username) => (
                    <Bar 
                      key={username} 
                      dataKey={username} 
                      name={username} 
                      stackId="a"
                      fill={getUserColor(username)} 
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No data available for the selected date range
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={resetFilters}
                  startIcon={<DateRangeIcon />}
                >
                  Reset Date Filter
                </Button>
              </Box>
            )}
          </Paper>

          {/* User Research Stats Metrics */}
          <Paper sx={{ p: 2, mb: 3, height: 400 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                User Research Performance
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {appliedFilters.chartDateRange === 'all' ? 'All Time' : 
                 appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                 appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                 appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                 appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                 appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
              </Typography>
            </Box>
            {Object.keys(userStatistics).length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart 
                  data={Object.entries(userStatistics).map(([username, stats]) => ({
                    name: username,
                    total: stats.total_searches || 0,
                    successful: stats.successful_searches || 0,
                    successRate: parseFloat(stats.success_rate) || 0,
                    color: getUserColor(username) // Add color based on username
                  }))}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" />
                  <ChartTooltip />
                  <Legend />
                  <Bar 
                    yAxisId="left" 
                    dataKey="total" 
                    name="Total Searches" 
                    fill="#8884d8"
                  >
                    {
                      Object.entries(userStatistics).map(([username, stats], index) => (
                        <Cell key={`cell-total-${index}`} fill={getUserColor(username)} />
                      ))
                    }
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="successRate" name="Success Rate (%)" stroke="#ff7300" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                <Typography variant="body1" color="text.secondary">
                  No user performance data available
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Overall User Research Stats Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell align="right">Total Searches</TableCell>
                  <TableCell align="right">Successful</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(userStatistics).map(([username, stats]) => (
                  <TableRow key={username} hover>
                    <TableCell>{username}</TableCell>
                    <TableCell align="right">{stats.total_searches}</TableCell>
                    <TableCell align="right">{stats.successful_searches}</TableCell>
                    <TableCell align="right">{stats.total_searches - stats.successful_searches}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${stats.success_rate}%`} 
                        color={parseFloat(stats.success_rate) > 80 ? "success" : parseFloat(stats.success_rate) > 50 ? "warning" : "error"}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Research History Tab */}
      {tabValue === 2 && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1 }} /> 
              Filters
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>User</InputLabel>
                  <Select
                    name="username"
                    value={filters.username}
                    label="User"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All Users</MenuItem>
                    {(isSupervisor ? teamMembers : users).map(user => (
                      <MenuItem key={user.username} value={user.username}>
                        {user.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Company"
                  name="company"
                  value={filters.company}
                  onChange={handleFilterChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    name="dateRange"
                    value={filters.dateRange}
                    label="Date Range"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="thisWeek">This Week</MenuItem>
                    <MenuItem value="lastWeek">Last Week</MenuItem>
                    <MenuItem value="thisMonth">This Month</MenuItem>
                    <MenuItem value="lastMonth">Last Month</MenuItem>
                    <MenuItem value="last3Months">Last 3 Months</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="success"
                    value={filters.success}
                    label="Status"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Success</MenuItem>
                    <MenuItem value="false">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="research_type"
                    value={filters.research_type}
                    label="Type"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="full_research">Full Research</MenuItem>
                    <MenuItem value="url_search">URL Search</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    onClick={resetFilters}
                    fullWidth
                    startIcon={<RefreshIcon />}
                  >
                    Reset Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Results count */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              Showing {researchLogs.length} research {researchLogs.length === 1 ? 'record' : 'records'} 
              {totalRecords > 0 ? ` of ${totalRecords} total` : ''}
              {appliedFilters.dateRange !== 'all' && (
                <span> (filtered by date: {
                  appliedFilters.dateRange === 'thisWeek' ? 'This Week' :
                  appliedFilters.dateRange === 'lastWeek' ? 'Last Week' :
                  appliedFilters.dateRange === 'thisMonth' ? 'This Month' :
                  appliedFilters.dateRange === 'lastMonth' ? 'Last Month' :
                  appliedFilters.dateRange === 'last3Months' ? 'Last 3 Months' : ''
                })</span>
              )}
            </Typography>
          </Box>
          
          {/* Research history table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {researchLogs.length > 0 ? (
                  researchLogs.map((log) => (
                    <TableRow 
                      key={log.id} 
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                      onClick={() => handleViewLogDetails(log)}
                    >
                      <TableCell>{formatDate(log.timestamp)}</TableCell>
                      <TableCell>{log.username || 'Anonymous'}</TableCell>
                      <TableCell>{log.company_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.research_type === 'url_search' ? 'URL Search' : 'Full Research'}
                          color={log.research_type === 'url_search' ? 'secondary' : 'primary'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{log.ip_address}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={log.success ? "Success" : "Failed"}
                          color={log.success ? "success" : "error"}
                          size="small"
                          icon={log.success ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLogDetails(log);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No research logs found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Rows per page:</Typography>
                <Select
                  value={recordsPerPage}
                  size="small"
                  onChange={(e) => {
                    setRecordsPerPage(e.target.value);
                    setCurrentPage(1); // Reset to first page when changing records per page
                    // Reset loading state to ensure we fetch with new records per page
                    setLoading(true);
                    setTimeout(() => {
                      fetchFilteredLogs(1);
                    }, 0);
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Box>
              
              <Pagination 
                count={totalPages} 
                page={currentPage} 
                onChange={handlePageChange} 
                color="primary" 
              />
            </Box>
          )}
        </>
      )}

      {/* Research Log Detail Dialog */}
      <Dialog
        open={logDialogOpen}
        onClose={handleCloseLogDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Research Details: {selectedLog && selectedLog.company_name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Basic Information</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body1"><strong>Company Name:</strong> {selectedLog.company_name}</Typography>
                  <Typography variant="body1"><strong>User:</strong> {selectedLog.username || 'Anonymous'}</Typography>
                  <Typography variant="body1"><strong>Timestamp:</strong> {formatDate(selectedLog.timestamp)}</Typography>
                  <Typography variant="body1">
                    <strong>Research Type:</strong>{' '}
                    <Chip
                      label={selectedLog.research_type === 'url_search' ? 'URL Search' : 'Full Research'}
                      color={selectedLog.research_type === 'url_search' ? 'secondary' : 'primary'}
                      size="small"
                      variant="outlined"
                    />
                  </Typography>
                  <Typography variant="body1"><strong>IP Address:</strong> {selectedLog.ip_address}</Typography>
                  <Typography variant="body1">
                    <strong>Status:</strong>{' '}
                    <Chip
                      label={selectedLog.success ? "Success" : "Failed"}
                      color={selectedLog.success ? "success" : "error"}
                      size="small"
                    />
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Search Parameters</Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                  {selectedLog.search_params ? (
                    <pre style={{ margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                      {JSON.stringify(selectedLog.search_params, null, 2)}
                    </pre>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No search parameters available
                    </Typography>
                  )}
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>User Agent</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {selectedLog.user_agent || 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResearchStatistics; 